import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import type { UserEntry } from '@common/types/users';
import { Alert, Button, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import DocumentSection, {
    type UploadedDocument,
} from '@/components/NewProspectModal/RetainerAgreement/DocumentSection';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useRetainerAgreements } from '@/queries/legalDocuments';
import {
    updateQuoteTemplateAttachments,
    updateQuoteTemplateDefaultRetainer,
} from '@/services/QuoteTemplatesService';
import { errorSnackbar } from '@/utils/snackbar';
import TemplateRetainerSection from './TemplateRetainerSection';

interface Props {
    quoteTemplateId: number | null;
    lawFirmId?: number;
    users: UserEntry[];
    practiceArea: ProspectPracticeArea | null;
    matterType: ProspectMatterType | null;
    defaultRetainerId: number | null;
    onDefaultRetainerChange(retainerAgreementId: number | null): void;
    /** Undefined until the saved attachments have loaded */
    attachments: UploadedDocument[] | undefined;
    onAttachmentsChange(attachments: UploadedDocument[]): void;
    isLoadError: boolean;
    onRetryLoad(): void;
}

const IntakeDocumentsSetup = forwardRef(function IntakeDocumentsSetup(
    {
        quoteTemplateId,
        lawFirmId,
        users,
        practiceArea,
        matterType,
        defaultRetainerId,
        onDefaultRetainerChange,
        attachments,
        onAttachmentsChange,
        isLoadError,
        onRetryLoad,
    }: Props,
    ref,
) {
    const { isSuperAdmin } = useLawFirmContext();
    const retainersQuery = useRetainerAgreements(lawFirmId);
    const firmRetainers = retainersQuery.data;
    const retainerOptions = (firmRetainers ?? []).filter(
        (retainer) =>
            retainer.userId === null &&
            retainer.practiceArea === practiceArea &&
            retainer.matterType === matterType,
    );
    // A saved retainer stops matching when it's deleted or the template's matter type changes
    const selectedRetainerId = retainerOptions.some(
        ({ retainerAgreementId }) => retainerAgreementId === defaultRetainerId,
    )
        ? defaultRetainerId
        : null;

    const submit = async (): Promise<{ success: boolean }> => {
        if (!quoteTemplateId) return { success: false };
        // Saving replaces the template's attachments, so never save without the saved ones
        if (!attachments) {
            errorSnackbar("The template's documents haven't loaded yet");
            return { success: false };
        }
        // Likewise, an unloaded list would clear the default retainer
        if (isSuperAdmin && !firmRetainers) {
            errorSnackbar(
                retainersQuery.isError
                    ? "The firm's legal documents couldn't be loaded"
                    : "The firm's legal documents haven't loaded yet",
            );
            return { success: false };
        }

        try {
            await Promise.all([
                updateQuoteTemplateAttachments(quoteTemplateId, {
                    fileIds: attachments.map(({ id }) => id),
                }),
                isSuperAdmin &&
                    updateQuoteTemplateDefaultRetainer(quoteTemplateId, {
                        retainerAgreementId: selectedRetainerId,
                    }),
            ]);
            return { success: true };
        } catch (error: unknown) {
            console.error(error);
            errorSnackbar('Something went wrong!');
            return { success: false };
        }
    };

    useImperativeHandle(ref, () => ({ submit }));

    return (
        <Stack
            padding={3}
            sx={{
                maxHeight: 'calc(100vh - 168px)',
                overflow: 'auto',
            }}
        >
            <Stack
                spacing={3}
                sx={{
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'neutral.borderColor',
                    bgcolor: 'neutral.white',
                    padding: '24px',
                    height: 'fit-content',
                    margin: '32px',
                }}
            >
                <Stack spacing={1}>
                    <Typography variant="h2">Documents</Typography>
                    <Typography variant="body2">
                        Every prospect created from this template starts with these documents, which
                        are included with its intake email. When creating a prospect, you can still
                        pick another legal document, remove the files, or add others.
                    </Typography>
                </Stack>
                <Divider orientation="horizontal" />
                <TemplateRetainerSection
                    lawFirmId={lawFirmId}
                    users={users}
                    practiceArea={practiceArea}
                    matterType={matterType}
                    retainers={firmRetainers && retainerOptions}
                    isLoadError={retainersQuery.isError}
                    onRetryLoad={() => void retainersQuery.refetch()}
                    value={selectedRetainerId}
                    onValueChange={onDefaultRetainerChange}
                    disabled={!isSuperAdmin}
                />
                <Paper
                    variant="outlined"
                    sx={{
                        borderColor: 'neutral.borderColor',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}
                >
                    {attachments ? (
                        <DocumentSection
                            value={attachments}
                            onValueChange={onAttachmentsChange}
                            lawFirmId={lawFirmId}
                        />
                    ) : isLoadError ? (
                        <Alert
                            severity="error"
                            action={
                                <Button color="inherit" size="small" onClick={onRetryLoad}>
                                    Retry
                                </Button>
                            }
                        >
                            The template's documents couldn't be loaded.
                        </Alert>
                    ) : (
                        <Stack alignItems="center" sx={{ padding: 4 }}>
                            <CircularProgress size={32} />
                        </Stack>
                    )}
                </Paper>
            </Stack>
        </Stack>
    );
});

export default IntakeDocumentsSetup;
